// Pure helpers for the interactive zone editors: the locked-sum weight sliders for the
// relative-zone customize panel, the %→Mahalanobis-multiplier mapping, and expanding per-KEY
// weights across the one-hot columns a categorical soil-type layer produces.
import { CATEGORICAL_DESIGN } from "./samplingDesign";
import { MAX_ZONES } from "./zoneDesign";

export type WeightMap = Record<string, number>; // key → weight PERCENT (the whole map sums to ~100)

const clampPct = (p: number) =>
  Number.isFinite(p) ? Math.max(0, Math.min(100, p)) : 0;

export function redistributeWeights(
  weights: WeightMap,
  key: string,
  pct: number,
): WeightMap {
  const target = clampPct(pct);
  const others = Object.keys(weights).filter((k) => k !== key);
  if (others.length === 0) return { [key]: 100 };
  const rest = 100 - target;
  const otherSum = others.reduce((s, k) => s + (weights[k] ?? 0), 0);
  const out: WeightMap = { [key]: target };
  if (otherSum <= 0) {
    const eq = rest / others.length;
    for (const k of others) out[k] = eq;
  } else {
    for (const k of others) out[k] = ((weights[k] ?? 0) * rest) / otherSum;
  }
  return out;
}

// Equal split across `keys` (the default — "all should be equal").
export function equalWeights(keys: string[]): WeightMap {
  const eq = keys.length ? 100 / keys.length : 0;
  return Object.fromEntries(keys.map((k) => [k, eq]));
}

// Check a new layer: it joins at the equal share (100/N_new); the existing layers rescale to fill the rest
// keeping their relative proportions.
export function addWeight(weights: WeightMap, key: string): WeightMap {
  if (key in weights) return weights;
  const n = Object.keys(weights).length + 1;
  return redistributeWeights({ ...weights, [key]: 0 }, key, 100 / n);
}

// Uncheck a layer: drop it and rescale the rest to 100 (keeping their proportions).
export function removeWeight(weights: WeightMap, key: string): WeightMap {
  const rest = Object.entries(weights).filter(([k]) => k !== key);
  if (!rest.length) return {};
  const sum = rest.reduce((s, [, v]) => s + v, 0);
  if (sum <= 0) {
    const eq = 100 / rest.length;
    return Object.fromEntries(rest.map(([k]) => [k, eq]));
  }
  return Object.fromEntries(rest.map(([k, v]) => [k, (v * 100) / sum]));
}

// Percent → Mahalanobis multiplier: the equal share (100/N) maps to 1.0 (so equal weights ===
// today's zones) a layer at m× the equal share gets multiplier m. Distance scales with the
// multiplier (variance with its square), so a doubled % pulls ~4× harder — visibly "further apart"
// as asked.
export function weightMultiplier(pct: number, nKeys: number): number {
  if (nKeys <= 0) return 1;
  const eq = 100 / nKeys;
  return eq > 0 ? clampPct(pct) / eq : 1;
}

// Expand per-KEY weights across the EXACT columns that `expandCategoricalFeatures` produces: a numeric key → its
// one column at the key's multiplier; a categorical (mapunit/taxon/series) key → one column per one-hot class
// each carrying the key's multiplier. Returns a per-column weight array index-aligned with the expanded
// features, so it can go straight into `designZones({ weights })`.
export function expandDesignWeights(
  keys: string[],
  features: (number | null)[][],
  multiplierForKey: (key: string) => number,
  maxClasses = 5,
): number[] {
  const out: number[] = [];
  features.forEach((col, i) => {
    const key = keys[i] ?? "";
    const m = multiplierForKey(key);
    if (!CATEGORICAL_DESIGN.test(key)) {
      out.push(m);
      return;
    }
    // mirror expandCategoricalFeatures' class count — SAME maxClasses, or the weight columns misalign
    const freq = new Map<number, number>();
    for (const v of col)
      if (v != null && Number.isFinite(v)) freq.set(v, (freq.get(v) ?? 0) + 1);
    const classes = Math.min(maxClasses, freq.size);
    for (let c = 0; c < classes; c++) out.push(m);
  });
  return out;
}

// Two regimes a selection that is EXACTLY ONE categorical layer (soil type — mapunit/taxon/series):
// the recommendation is the number of distinct classes PRESENT ON THE FIELD, so "Pure Soil Type"
// recommends exactly as many zones as there are soil types. Clamped to [2, MAX_ZONES]: k-means
// needs 2, and a one-type field genuinely has one zone's worth of signal but the engine floor is 2
// (disclosed here rather than special-cased everywhere downstream).
export const CATEGORICAL_FULL_CLASSES = 40; // MAX_STRING_CLASSES — the encoder's own ceiling

export function categoricalOnlySelection(checked: string[]): boolean {
  return checked.length === 1 && CATEGORICAL_DESIGN.test(checked[0]!);
}

export function recommendedZoneCount(
  checked: string[],
  features: (number | null)[][],
): number | null {
  if (!categoricalOnlySelection(checked)) return null;
  const col = features[0] ?? [];
  const distinct = new Set<number>();
  for (const v of col) if (v != null && Number.isFinite(v)) distinct.add(v);
  if (distinct.size === 0) return null;
  return Math.max(2, Math.min(40, distinct.size));
}

// density → count rounds UP: a requested density is a MAXIMUM zone size ("22 ac/zone" must not
// quietly deliver 30-ac zones) — the same round-up rule as the decile acres÷22 default.
export function densityToK(acres: number, acPerZone: number): number | null {
  if (!(acres > 0) || !(acPerZone > 0)) return null;
  return Math.max(2, Math.min(MAX_ZONES, Math.ceil(acres / acPerZone)));
}

/* * count → density: the plain quotient (display rounds; the stored value never does) */
export function kToDensity(acres: number, k: number): number | null {
  return acres > 0 && k > 0 ? acres / k : null;
}

/* * A zone count the stepper can store: at least 2, at most the design ceiling, or unset. */
export function clampK(k: number | null | undefined): number | null {
  return k == null || !Number.isFinite(k)
    ? null
    : Math.max(2, Math.min(MAX_ZONES, Math.round(k)));
}
